import { Component, ChangeDetectionStrategy } from '@angular/core';
import { TooltipRow } from '../shared';
import { AgRendererComponent } from 'ag-grid-angular/main';
import { LabTestResultGridRowItem } from '../on-screen-results/grid-row-item';
import { isEmpty, sortBy, uniq, groupBy, keys } from 'lodash';
import { DataRevisionIndicator } from '../shared';
import { ResultData, VisitResult } from './subject-pivot-interfaces';
import { ModalData } from '../on-screen-results/standard-test-results/model-dialog/data-revision/data-revision.component';
import { ModalDialogService } from '../on-screen-results/standard-test-results/model-dialog/ModalDialogService';

interface SubjectPivotVisitCellData {
    result: string;
    resultData: ResultData;
    resultExists: boolean;
    commentExists: boolean;
    tooltipContent: TooltipRow[];
}

@Component({
    selector: 'lab-tests-grid-visit',
    template: require('./lab-tests-grid-visit.component.html'),
    styles: [require('./lab-tests-grid-visit.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class LabTestGridVisitComponent implements AgRendererComponent {
    public resultClass: string;

    public cellData: SubjectPivotVisitCellData[];
    public protocol: string;
    public participantNumber: string;
    public investigatorNumber: string;
    constructor(private dataRevisionIndicator: DataRevisionIndicator, public modalDialogService: ModalDialogService) { }

    public agInit(params: LabTestResultGridRowItem): void {

        this.getVisitResultValue(params);
    }

    public refresh() {
        return false;
    }

    public async openpopup(labtestId: number, accessionNumber: string, visitDescription: string) {
        let data: ModalData = {
            title: 'Data Change',
            data: [],
            protocol: this.protocol,
            participantNumber: this.participantNumber,
            investigatorNumber: this.investigatorNumber,
            accessionNumber: accessionNumber,
            visitDescription: visitDescription,
            closeButtonText: 'Close',
            reverseButtons: true,
            displayClose: false,
            type: 'result',
            labTestId: labtestId
        };
        try {
            await this.modalDialogService.showDialog(data).toPromise();
        } catch (error) {
            console.error('Error showing modal dialog:', error);
        }
    }

    private getVisitResultValue(params: LabTestResultGridRowItem): void {
        if (!params || !params.data) {
            return;
        }

        const testId = params.data.uniqLabTestIdKey;
        const cellParams = (params.colDef as VisitResult).cellRendererParams;
        const visitId = cellParams.visitId;
        const theCellValues = cellParams.cellValues;
        const testResultIds = cellParams.testResultIds;

        this.protocol = 'Loading..';
        this.participantNumber = params.data.subjectNumber;
        this.investigatorNumber = 'Loading..';

        if (!theCellValues || !theCellValues[testId] || !theCellValues[testId][visitId]) {
            return;
        }

        this.cellData = [];

        const cellValues = theCellValues[testId][visitId];

        if (!testResultIds[testId] || !testResultIds[testId][visitId]) {
            return;
        }

        testResultIds[testId][visitId].forEach(item => {

            const data = cellValues[item];

            if (data) {
                const commentExists = !isEmpty(data.comments);
                const cellFlags: string[] = [];
                const tooltipContent: TooltipRow[] = [];

                if (data.isCancelled) {
                    tooltipContent.push({
                        title: 'Cancellation Reason',
                        value: data.cancellationReason
                    });
                } else {
                    tooltipContent.push({
                        title: 'Value',
                        value: data.result
                    });

                    if (data.isFlagged) {
                        const flagGroups = groupBy(data.flags, f => f.category);

                        keys(flagGroups).sort().forEach((category: string) => {
                            const flagNames = flagGroups[category].map(f => f.name);
                            const flags = uniq(flagNames).sort().join(' ');
                            tooltipContent.push({
                                title: category,
                                value: flags
                            });
                            cellFlags.push(flags);
                        });
                    }
                }

                this.addCommentsToTooltip(data, commentExists, tooltipContent);

                if (data.hasDataRevision) {
                    tooltipContent.push({
                        title: 'Revision',
                        value: this.dataRevisionIndicator.resultDataRevisionMessage
                    });
                }

                const cellValue = {
                    result: `${data.result} ${cellFlags.join(', ')}`,
                    resultData: data,
                    resultExists: !data.isCancelled && !isEmpty(data.result),
                    tooltipContent: tooltipContent,
                    commentExists: commentExists
                } as SubjectPivotVisitCellData;

                this.cellData.push(cellValue);
            }
        });

        this.cellData = sortBy(this.cellData, d => d.resultData.sequence);
    }

    private addCommentsToTooltip(data: ResultData, commentExists: boolean, tooltipContent: TooltipRow[]) {
        if (commentExists) {
            tooltipContent.push({
                title: 'Comment',
                value: data.comments
            });
        }
    }
}
